import { type MouseEvent, useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router";
import { inApp, onFromApp, postToApp } from "../../lib/inApp";
import { useSending } from "../../sending/SendingContext";
import { sendingNavDot } from "../../sending/sendingProtection";
import { useLayout } from "../season/layout";
import { cx } from "../ui/cx";
import { Icon, type IconName } from "../ui/Icon";
import { useUnreadCount } from "./unread";

/** The five places, for the phone's tab bar and the iPad's top bar. */
export const TABS: ReadonlyArray<{ id: string; to: string; label: string; icon: IconName; end?: boolean }> = [
  { id: "home", to: "/", label: "Home", icon: "home", end: true },
  { id: "rooms", to: "/rooms", label: "Rooms", icon: "play" },
  { id: "people", to: "/people", label: "People", icon: "users" },
  { id: "conversations", to: "/conversations", label: "Conversations", icon: "chat" },
  { id: "more", to: "/more", label: "More", icon: "more" },
];

/** The screens a tab opens. Any screen pushed on top of one hides the bar, as on the phone. */
export const TAB_ROOTS = TABS.map((t) => t.to);

export function isTabRoot(pathname: string): boolean {
  const p = pathname.replace(/\/+$/, "") || "/";
  return TAB_ROOTS.includes(p);
}

/** Where tab `id` opens, inside the workspace. */
export function tabRoot(id: string): string | null {
  return TABS.find((t) => t.id === id)?.to ?? null;
}

/** The tab whose area a path is in: More holds Assets, Automations and Settings. */
export function tabOf(pathname: string): string | null {
  const first = pathname.split("/").filter(Boolean)[0] ?? "";
  if (first === "") return "home";
  if (first === "assets" || first === "automations" || first === "settings") return "more";
  return TABS.find((t) => t.to === `/${first}`)?.id ?? null;
}

/**
 * What pressing a tab does, for the phone's tab bar and the iPad's top bar alike. Inside the Flashsuite phone app
 * every tab is its own kept screen, so a press on another tab asks the app for that tab's screen instead of moving
 * this one there, and lights that tab at once while the app opens it (flashsuite-phone docs/APP-MODE.md sections 3
 * and 10). Outside the app a press is a plain link. `lit` is the tab pressed, until the app hides or shows this
 * screen, or the page moves.
 */
export function useAppTabs(): { lit: string | null; pressed: (id: string) => (e: MouseEvent<HTMLAnchorElement>) => void } {
  const { pathname } = useLocation();
  const [asked, setAsked] = useState<{ id: string; at: string } | null>(null);
  useEffect(
    () =>
      onFromApp((m) => {
        if (m.kind === "hidden" || m.kind === "shown") setAsked(null);
      }),
    [],
  );
  const current = tabOf(pathname);
  const pressed = (id: string) => (e: MouseEvent<HTMLAnchorElement>) => {
    if (!inApp() || id === current) return;
    e.preventDefault();
    postToApp({ v: 1, kind: "tab", tab: id, path: e.currentTarget.getAttribute("href") ?? "/" });
    setAsked({ id, at: pathname });
  };
  return { lit: asked?.at === pathname ? asked.id : null, pressed };
}

/**
 * The tab bar docked at the bottom of a phone: the four daily places and More for the
 * rest. Shown only on a phone, and only on a tab's own screen.
 */
export function TabBar() {
  const { pathname } = useLocation();
  const phone = useLayout() === "phone";
  const unread = useUnreadCount(phone);
  const { status } = useSending();
  const navDot = status ? sendingNavDot(status.state) : null;
  const { lit, pressed } = useAppTabs();
  if (!phone || !isTabRoot(pathname)) return null;

  return (
    <nav className="dock" aria-label="Main">
      {TABS.map(({ id, to, label, icon, end }) => (
        <NavLink
          key={to}
          to={to}
          end={end}
          onClick={pressed(id)}
          className={({ isActive }) => ((lit ? id === lit : isActive) ? "on" : undefined)}
          aria-label={to === "/conversations" && unread ? `${label}, ${unread} unread` : label}
        >
          <Icon name={icon} />
          <span>{label}</span>
          {to === "/conversations" && unread ? (
            <b className="badge" aria-hidden="true">
              {unread > 99 ? "99+" : unread}
            </b>
          ) : null}
          {id === "more" && navDot ? <span className={cx("navdot", navDot)} aria-label="Sending needs a look" /> : null}
        </NavLink>
      ))}
    </nav>
  );
}
